import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarDays, Send, Loader2, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "Daytime Support",
  "Lactation & Feeding Support",
  "Newborn Care Education",
  "Meal Prep & Light Household Support",
  "Sibling Support",
  "Not sure yet — please advise",
];

export default function Booking() {
  return (
    <section
      id="booking"
      data-testid="booking-section"
      className="py-24 md:py-32"
      style={{ background: "var(--bg-primary)" }}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-12">
          <span className="section-eyebrow">Begin your care</span>
          <span className="gold-divider ml-3 mb-2 align-middle" />
          <h2
            className="font-serif-display mt-4 text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
            style={{ color: "var(--text-main)" }}
            data-testid="booking-headline"
          >
            Book a free <em className="not-italic" style={{ color: "var(--blush)" }}>consultation.</em>
          </h2>
          <p className="mt-5 leading-relaxed" style={{ color: "var(--text-muted)" }}>
            Choose a date and time, or send a thoughtful inquiry — Monica will reach out personally.
          </p>
        </div>

        <div
          className="rounded-3xl p-6 md:p-10 lg:p-14 brand-soft-shadow"
          style={{ background: "var(--bg-secondary)", border: "1px solid var(--border-soft)" }}
        >
          <Tabs defaultValue="schedule" className="w-full">
            <TabsList
              className="grid grid-cols-2 w-full md:w-[420px] rounded-full p-1 mb-10"
              style={{ background: "var(--bg-tertiary)" }}
              data-testid="booking-tabs"
            >
              <TabsTrigger
                value="schedule"
                data-testid="tab-schedule"
                className="rounded-full data-[state=active]:bg-[var(--blush)] data-[state=active]:text-white text-sm"
              >
                <CalendarDays size={14} className="mr-2" /> Schedule a slot
              </TabsTrigger>
              <TabsTrigger
                value="inquiry"
                data-testid="tab-inquiry"
                className="rounded-full data-[state=active]:bg-[var(--blush)] data-[state=active]:text-white text-sm"
              >
                <Send size={14} className="mr-2" /> Send an inquiry
              </TabsTrigger>
            </TabsList>

            <TabsContent value="schedule">
              <ScheduleConsultation />
            </TabsContent>
            <TabsContent value="inquiry">
              <InquiryForm />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </section>
  );
}

function InquiryForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    due_date: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please fill in your name, email, service and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings/inquiry`, form);
      toast.success("Inquiry sent! Monica will reach out within 24–48 hours.");
      setForm({ name: "", email: "", phone: "", due_date: "", service: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "rounded-xl h-12 bg-white border-[var(--border-soft)] focus-visible:ring-[var(--blush)]";

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="inquiry-form">
      <div>
        <Label htmlFor="name" className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Name</Label>
        <Input id="name" data-testid="inquiry-name" value={form.name} onChange={(e) => onChange("name", e.target.value)}
          className={`mt-2 ${inputCls}`} placeholder="Your full name" />
      </div>
      <div>
        <Label htmlFor="email" className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Email</Label>
        <Input id="email" type="email" data-testid="inquiry-email" value={form.email} onChange={(e) => onChange("email", e.target.value)}
          className={`mt-2 ${inputCls}`} placeholder="you@email.com" />
      </div>
      <div>
        <Label htmlFor="phone" className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Phone (optional)</Label>
        <Input id="phone" data-testid="inquiry-phone" value={form.phone} onChange={(e) => onChange("phone", e.target.value)}
          className={`mt-2 ${inputCls}`} placeholder="0412 345 678" />
      </div>
      <div>
        <Label htmlFor="due-date" className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Due / baby's birth date</Label>
        <Input id="due-date" type="date" data-testid="inquiry-due-date" value={form.due_date} onChange={(e) => onChange("due_date", e.target.value)}
          className={`mt-2 ${inputCls}`} />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Service of interest</Label>
        <Select value={form.service} onValueChange={(v) => onChange("service", v)}>
          <SelectTrigger data-testid="inquiry-service" className={`mt-2 ${inputCls}`}>
            <SelectValue placeholder="Choose a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_OPTIONS.map((s) => (
              <SelectItem key={s} value={s} data-testid={`service-option-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <Label htmlFor="message" className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Your message</Label>
        <Textarea
          id="message"
          data-testid="inquiry-message"
          value={form.message}
          onChange={(e) => onChange("message", e.target.value)}
          className="mt-2 rounded-xl min-h-[140px] bg-white border-[var(--border-soft)] focus-visible:ring-[var(--blush)]"
          placeholder="Tell us a little about your family, what you're navigating, and how we might help…"
        />
      </div>
      <div className="md:col-span-2 flex items-center justify-between flex-wrap gap-4">
        <p className="text-xs" style={{ color: "var(--text-muted)" }}>
          We respond within 24–48 hours. A confirmation will be sent to your email.
        </p>
        <Button
          type="submit"
          disabled={submitting}
          data-testid="inquiry-submit"
          className="rounded-full h-12 px-8 transition-transform hover:scale-105"
          style={{ background: "var(--blush)", color: "#FAF9F6" }}
        >
          {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
          {submitting ? "Sending…" : "Send inquiry"}
        </Button>
      </div>
    </form>
  );
}

function ScheduleConsultation() {
  const [date, setDate] = useState();
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!date) {
      setSlots([]);
      setSelectedSlot("");
      return;
    }
    const d = format(date, "yyyy-MM-dd");
    setLoadingSlots(true);
    setSelectedSlot("");
    axios
      .get(`${API}/bookings/availability`, { params: { date: d } })
      .then((res) => setSlots(res.data.slots || []))
      .catch(() => {
        toast.error("Couldn't load availability — please try again.");
        setSlots([]);
      })
      .finally(() => setLoadingSlots(false));
  }, [date]);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !selectedSlot) {
      toast.error("Please select a date and time slot.");
      return;
    }
    if (!form.name || !form.email) {
      toast.error("Please share your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        date: format(date, "yyyy-MM-dd"),
        time_slot: selectedSlot,
      };
      await axios.post(`${API}/bookings/consultation`, payload);
      toast.success(`Consultation booked for ${format(date, "EEEE, MMM d")} at ${selectedSlot}.`);
      setForm({ name: "", email: "", phone: "", notes: "" });
      setSelectedSlot("");
      // Refresh slots so the chosen one disappears
      try {
        const res = await axios.get(`${API}/bookings/availability`, {
          params: { date: format(date, "yyyy-MM-dd") },
        });
        setSlots(res.data.slots || []);
      } catch (_) {}
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Booking failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const inputCls = "rounded-xl h-12 bg-white border-[var(--border-soft)] focus-visible:ring-[var(--blush)]";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10" data-testid="schedule-form">
      <div>
        <h3 className="font-serif-display text-2xl mb-1" style={{ color: "var(--text-main)" }}>
          1. Pick a date
        </h3>
        <p className="text-sm mb-4" style={{ color: "var(--text-muted)" }}>
          Free 30-minute consultations, Mon–Sat.
        </p>
        <div
          className="rounded-2xl p-3 inline-block"
          style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border-soft)" }}
        >
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={(d) => d < today || d.getDay() === 0}
            data-testid="booking-calendar"
            className="rounded-xl"
          />
        </div>

        <h3 className="font-serif-display text-2xl mt-8 mb-3" style={{ color: "var(--text-main)" }}>
          2. Choose a time
        </h3>
        {!date && (
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Select a date to see available times.
          </p>
        )}
        {date && loadingSlots && (
          <p className="text-sm flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
            <Loader2 size={14} className="animate-spin" /> Loading times…
          </p>
        )}
        {date && !loadingSlots && slots.length === 0 && (
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            No times available — please try another day.
          </p>
        )}
        {date && !loadingSlots && slots.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="time-slots">
            {slots.map((s) => {
              const active = selectedSlot === s;
              return (
                <button
                  type="button"
                  key={s}
                  data-testid={`slot-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  onClick={() => setSelectedSlot(s)}
                  className="rounded-full h-11 text-sm font-medium transition-all"
                  style={{
                    background: active ? "var(--blush)" : "var(--bg-tertiary)",
                    color: active ? "#FAF9F6" : "var(--text-main)",
                    border: `1px solid ${active ? "var(--blush)" : "var(--border-soft)"}`,
                  }}
                >
                  <Clock size={12} className="inline mr-2 -mt-0.5" /> {s}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <form onSubmit={submit} className="space-y-5">
        <h3 className="font-serif-display text-2xl mb-1" style={{ color: "var(--text-main)" }}>
          3. Your details
        </h3>
        <div>
          <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Name</Label>
          <Input data-testid="schedule-name" value={form.name} onChange={(e) => onChange("name", e.target.value)}
            className={`mt-2 ${inputCls}`} placeholder="Your full name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Email</Label>
          <Input type="email" data-testid="schedule-email" value={form.email} onChange={(e) => onChange("email", e.target.value)}
            className={`mt-2 ${inputCls}`} placeholder="you@email.com" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Phone (optional)</Label>
          <Input data-testid="schedule-phone" value={form.phone} onChange={(e) => onChange("phone", e.target.value)}
            className={`mt-2 ${inputCls}`} placeholder="0412 345 678" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest" style={{ color: "var(--text-muted)" }}>Notes (optional)</Label>
          <Textarea
            data-testid="schedule-notes"
            value={form.notes}
            onChange={(e) => onChange("notes", e.target.value)}
            className="mt-2 rounded-xl min-h-[110px] bg-white border-[var(--border-soft)] focus-visible:ring-[var(--blush)]"
            placeholder="Anything you'd like Monica to know ahead of the call?"
          />
        </div>

        <div
          className="rounded-2xl p-4 text-sm"
          style={{ background: "var(--bg-tertiary)", border: "1px dashed var(--gold)" }}
          data-testid="schedule-summary"
        >
          {date && selectedSlot ? (
            <span style={{ color: "var(--text-main)" }}>
              <b>Confirming:</b> {format(date, "EEEE, MMM d, yyyy")} at <b>{selectedSlot}</b>
            </span>
          ) : (
            <span style={{ color: "var(--text-muted)" }}>
              Pick a date and time to continue.
            </span>
          )}
        </div>

        <Button
          type="submit"
          disabled={submitting || !date || !selectedSlot}
          data-testid="schedule-submit"
          className="w-full rounded-full h-12 transition-transform hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
          style={{ background: "var(--blush)", color: "#FAF9F6" }}
        >
          {submitting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <CalendarDays size={16} className="mr-2" />}
          {submitting ? "Booking…" : "Confirm consultation"}
        </Button>
      </form>
    </div>
  );
}
